<template>
  <div class="admin-container">
    <!-- Header -->
    <v-card class="mb-6 admin-header">
      <v-card-text class="pa-6">
        <div class="d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon size="32" color="primary" class="mr-3">mdi-store-settings</v-icon>
            <div>
              <h2 class="text-h5 font-weight-bold primary--text">Painel Administrativo</h2>
              <p class="text-subtitle-1 mb-0 grey--text">Gerencie todas as lojas do sistema</p>
            </div>
          </div>
          <v-btn
            color="primary"
            @click="addStore"
            large
            class="add-store-btn"
            elevation="2"
          >
            <v-icon left>mdi-plus</v-icon>
            Adicionar Loja
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Search and Filters -->
    <v-card class="mb-4" elevation="2" rounded="xl">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Buscar por nome da loja ou proprietário"
              outlined
              dense
              clearable
              hide-details
              class="search-field"
            />
          </v-col>
          <v-col cols="12" md="6" class="text-right">
            <v-chip class="mr-2" color="info">
              <v-icon left small>mdi-store</v-icon>
              {{ filteredStores.length }} lojas
            </v-chip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Data Table -->
    <v-card elevation="3" class="stores-table">
      <v-data-table
        :headers="headers"
        :items="filteredStores"
        :items-per-page="15"
        :search="search"
        class="elevation-0"
        :loading="loading"
        loading-text="Carregando lojas..."
      >
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="32" class="mr-3" color="primary">
              <v-icon color="white">mdi-store</v-icon>
            </v-avatar>
            <div>
              <span v-if="item.name" class="font-weight-medium">{{ item.name }}</span>
              <small v-else class="font-italic grey--text">Loja não inicializada</small>
            </div>
          </div>
        </template>

        <template v-slot:item.owner="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="28" class="mr-2" color="secondary">
              <v-icon size="16" color="white">mdi-account</v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-medium">{{ item.owner || 'N/A' }}</div>
              <small class="grey--text">{{ item.ownerEmail || 'Email não informado' }}</small>
            </div>
          </div>
        </template>

        <template v-slot:item.users="{ item }">
          <v-chip small color="info" outlined>
            <v-icon left small>mdi-account-group</v-icon>
            {{ item.users }}
          </v-chip>
        </template>

        <template v-slot:item.clients="{ item }">
          <v-chip small color="success" outlined>
            <v-icon left small>mdi-account-multiple</v-icon>
            {{ item.clients || 0 }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                @click="editOwner(item)"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-account-edit</v-icon>
              </v-btn>
            </template>
            <span>Editar Proprietário</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals -->
    <AddStoreModal @refresh="getStores" />
    <EditOwnerModal @refresh="getStores" />
  </div>
</template>

<script>
import adminService from "@/services/admin";
import AddStoreModal from "@/components/admin/AddStoreModal";
import EditOwnerModal from "@/components/admin/EditOwnerModal";

export default {
  components: {
    AddStoreModal,
    EditOwnerModal,
  },
  data() {
    return {
      stores: [],
      search: "",
      loading: false,
      headers: [
        { text: "Loja", value: "name", sortable: true },
        { text: "Proprietário", value: "owner", sortable: true },
        { text: "Usuários", value: "users", align: "center", sortable: true },
        { text: "Clientes", value: "clients", align: "center", sortable: true },
        { text: "Ações", value: "actions", align: "center", sortable: false },
      ],
    };
  },
  computed: {
    filteredStores() {
      if (!this.search) return this.stores;
      
      const searchLower = this.search.toLowerCase();
      return this.stores.filter(store => {
        return (
          (store.name && store.name.toLowerCase().includes(searchLower)) ||
          (store.owner && store.owner.toLowerCase().includes(searchLower)) ||
          (store.ownerEmail && store.ownerEmail.toLowerCase().includes(searchLower))
        );
      });
    },
  },
  mounted() {
    this.getStores();
  },
  methods: {
    addStore() {
      this.$root.$emit("open-add-store-modal");
    },
    editOwner(store) {
      this.$root.$emit("open-edit-owner-modal", store);
    },
    async getStores() {
      try {
        this.loading = true;
        const response = await adminService.stores.getAll();
        this.stores = response.stores.map((store) => {
          const owner = store.UserStore.find((user) => user.isOwner)?.User;
          return {
            ...store,
            name: store.Condominium?.name,
            owner: owner?.name,
            ownerEmail: owner?.email,
            ownerPhone: owner?.phone,
            users: store._count.UserStore,
            clients: store.Condominium?._count.Clients,
          };
        });
      } catch (error) {
        console.error(error);
        this.$toast.error("Erro ao carregar lojas");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-container {
  padding: 20px;
  background-color: #F6F9FC;
  min-height: 100vh;
}

.admin-header {
  background-color: #ffffff;
  color: #424242;
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.admin-header .v-card-text {
  background: #ffffff;
}

.add-store-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.search-field {
  border-radius: 12px;
}

.search-field >>> .v-input__control .v-input__slot {
  background: #ffffff;
  border-radius: 12px;
}

.stores-table {
  border-radius: 16px !important;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stores-table >>> .v-data-table thead tr th {
  background: #F6F9FC;
  color: #424242 !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  border: none !important;
  border-bottom: 2px solid #e0e0e0 !important;
}

.stores-table >>> .v-data-table tbody tr {
  transition: all 0.3s ease;
}

.stores-table >>> .v-data-table tbody tr:hover {
  background:rgb(255, 255, 255);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stores-table >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #e0e0e0;
  padding: 16px;
}

.v-avatar {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.v-chip {
  font-weight: 500;
  border-radius: 8px;
}

.v-btn--icon {
  transition: all 0.3s ease;
}

.v-btn--icon:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

@media (max-width: 960px) {
  .admin-container {
    padding: 10px;
  }
  
  .admin-header .v-card-text {
    padding: 16px !important;
  }
  
  .admin-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
  }
  
  .admin-header .add-store-btn {
    margin-top: 16px;
  }
}
</style>
